import { useState, useEffect } from 'react';
import { getCampLocations } from '../firebase/firebase';

import { ReactComponent as ChautauquaArrowSVG } from './chautauqua-arrow.svg';
import { ReactComponent as SearchIcon } from './search-icon.svg';
import styles from './component-styles.module.css';
import { notEmpty } from '..';
import levenshtein from 'js-levenshtein';


export function PhoneNumberInput({phoneNumber, setPhoneNumber, id}) {

    const phoneKeydownListener = (evt) => {
        if (!(evt.ctrlKey || evt.key.length > 1 || /[0-9.]/.test(evt.key)))
            evt.preventDefault();
    }

    const phoneKeyUpListener = (evt) => {
        const digits = evt.target.value.replace(/\D/g,'').substring(0,10);
        const areaCode = digits.substring(0,3);
        const prefix = digits.substring(3,6);
        const suffix = digits.substring(6,10);

        if(digits.length > 6) {evt.target.value = `(${areaCode}) ${prefix}-${suffix}`;}
        else if(digits.length > 3) {evt.target.value = `(${areaCode}) ${prefix}`;}
        else if(digits.length > 0) {evt.target.value = `(${areaCode}`;}
    }

    return (
        <input type="tel" autoComplete="tel-national" id={id}
            onChange={(e) => {setPhoneNumber(e.target.value); notEmpty(e);}} 
            onKeyDown={(e) => phoneKeydownListener(e)}
            onKeyUp={(e) => phoneKeyUpListener(e)} 
            placeholder='(123) 456-7890' defaultValue={phoneNumber}/>      
    )
}

export function LocationsSelector({setSelectedLocations, selectedLocations, dropdownVisibily, setDropdownVisibily}) {

    const [locations, setLocations] = useState([]);

    useEffect(() => {
        getCampLocations().then((locations) => {setLocations(locations)})
    }, [setLocations]);

    useEffect(() => {
        const parent = document.getElementById(styles.location_checkboxes);

        for (const element of parent.children)
            if(element.id !== styles.blocker)
                element.children[0].checked = selectedLocations.includes(element.children[0].id);
        
    }, [selectedLocations]);

    const onCheckboxChanged = (checked, location) => {
        if(checked) {
            setSelectedLocations([...selectedLocations, location])

        } else {
            let index = selectedLocations.indexOf(location);

            if(index >= 0)
                selectedLocations.splice(index, 1);

            setSelectedLocations([...selectedLocations]);
        }
    }

    var labelText = "No Locations Selected";

    if(selectedLocations.length >= 2)
        labelText = `${selectedLocations.length} Location${selectedLocations.length===1 ? '' : 's'} Selected`;
    else if (selectedLocations.length === 1)
        labelText = `${selectedLocations[0].split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}`;

    const onBlockerClicked = (e) => {
        setDropdownVisibily(false);
        const underneth = document.elementsFromPoint(e.clientX, e.clientY);

        if(underneth.length > 2 && underneth[1].click !== undefined) {
            underneth[1].click();
            underneth[1].focus();
        }
    };

    return (
        <div id={styles.locations}>
            <div id={styles.location_selector} onClick={() => setDropdownVisibily(!dropdownVisibily)}>
                {labelText}
                <ChautauquaArrowSVG fill="#000000"/>
            </div>
            <div id={styles.location_checkboxes} style={{display: (dropdownVisibily) ? 'flex' : 'none'}}>
                {
                    locations.map(
                        (location) => {
                            let tag = location;
                            return (
                            <label key={tag} htmlFor={tag}>
                                <input type="checkbox" id={tag} onChange={(ev) => onCheckboxChanged(ev.target.checked, tag)}/>
                                {` ${location}`}
                            </label>)
                        }
                    )
                }
            </div>

            <div id={styles.blocker} onClick={(e) => onBlockerClicked(e)} style={{display: (dropdownVisibily) ? 'flex' : 'none'}}/>
        </div>
    );
}

export function SearchableSelect({onChange, options, multiselect = true}) {

    const removeFormatting = (option) => {
        if(option !== undefined)
            return option.substring(0, option.indexOf('_')).trim();
        
        return option;
    }
    const addFormatting = (option) => {
        if(option !== undefined) {
            const split = option.split('_');
            if(split.length >= 2) {
                return (
                    <>
                        {split[0]}
                        <span style={{fontSize: '0.75rem', fontStyle: 'italic'}}>{split[1]}</span>
                    </> )
            }
        }
        
        return option;
    }

    const [visibility, setVisibility] = useState(false);
    const [selected, setSelected] = useState(multiselect ? [] : options[0]);
    const [search, setSearch] = useState("");

    useEffect(() => {if(onChange !== undefined) onChange(removeFormatting(selected));}, [selected]);
    useEffect(() => {
        if(visibility === false) {
            setSearch("");
            for(const element of document.getElementsByClassName(styles.search))
                element.value = "";
        }
           
    }, [visibility]);
    useEffect(() => {
        setSelected(multiselect ? [] : options[0]);
    }, [options]);

    const getLabelText = () => {
        if(multiselect) {
            if(selected.length === 0)
                return "None Selected";
            else if(selected.length === 1)
                return selected[0];
            else
                return `${selected.length} Selected` 
        } else
            return addFormatting(selected) ?? "No Selection";
    }

    

    const onCheckboxChanged = (checked, value) => {
        if(checked) {
            setSelected([...selected, value])

        } else {
            let index = selected.indexOf(value);

            if(index >= 0)
                selected.splice(index, 1);

            setSelected([...selected]);
        }
    }

    const onButtonClicked = (value) => {
        setVisibility(false);
        setSelected(value);
    }

    const getSearchedOptions = () => {
        var filtered = [...options];

        if(search !== "") {
            var minRank = Number.MAX_SAFE_INTEGER;

            for(const option of options)
                minRank = Math.min(rankSearch(option, search), minRank);
            
            filtered = filtered.filter((option) => (rankSearch(option, search) <= minRank + 2));

            filtered = filtered.sort((a, b) => {
                return rankSearch(a, search) - rankSearch(b, search);
            })
        }

        return filtered;
    }

    const onBlockerClicked = (e) => {
        setVisibility(false);
        const underneath = document.elementsFromPoint(e.clientX, e.clientY);

        if(underneath.length > 2 && underneath[1].click !== undefined) {
            underneath[1].click();
            underneath[1].focus();
        }
    };

    return (
        <div className={styles.searchable}>
            <div className={styles.click} onClick={() => setVisibility(!visibility)}>
                {getLabelText()}
                <ChautauquaArrowSVG fill="#000000"/>
            </div>
            <div className={styles.dropdown} style={{display: (visibility) ? 'flex' : 'none'}}>
                <div className={styles.search_box}>
                    <SearchIcon className={styles.search_icon}/>
                    <input name="search-input" className={styles.search} onChange={(e) => setSearch(e.target.value)} placeholder='Search' />
                </div>
                
                <div className="divider"></div>
                <div className={styles.list}>
                    {
                        multiselect ? 
                            getSearchedOptions().map(
                                (option) => 
                                    (
                                        <label key={option} htmlFor={option}>
                                            <input type="checkbox" id={option} onChange={(ev) => onCheckboxChanged(ev.target.checked, option)}/>
                                            {` ${addFormatting(option)}`}
                                        </label>
                                    )
                                
                            )
                            :
                            getSearchedOptions().map(
                                (option) => <div key={option} className={`${styles.option} ${(option === selected) ? styles.selected : ''}`} 
                                                    onClick={() => onButtonClicked(option)}>{addFormatting(option)}</div>
                            )
                    }
                </div>                
            </div>
            <div id={styles.blocker} onClick={(e) => onBlockerClicked(e)} style={{display: (visibility) ? 'flex' : 'none'}}/>
        </div>
    );
}

function rankSearch(option, search) {
    var score = 0;

    var searchWords = search.split(/\s/g);
    var optionWords = option.split(/\s/g);

    for(const searchWord of searchWords) {
        let min = Number.MAX_SAFE_INTEGER;
        
        for(const optionWord of optionWords)
            min = Math.min(levenshtein(optionWord.slice(0, searchWord.length), searchWord), min);

        score += min;
    }

    return score;
}